import { Identifier, RaRecord } from 'react-admin';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { CommissionStatus } from 'types/embedded/commission';
import { Currency } from 'types/embedded/currency';

type CommissionsFilterParams = {
  defaultStatus: RaRecord<Identifier>[];
  statusOptions: CommissionStatus[];
  merchantWatched: string;
};

export const useCommissionsFilterFields = ({
  defaultStatus,
  statusOptions,
  merchantWatched,
}: CommissionsFilterParams): FilterFieldConfig[] => [
  {
    name: 'name',
    label: 'Название',
    chipType: 'text',
    input: (
      <TextInput
        fullWidth={true}
        helperText={false}
        key="name"
        label="Название"
        resettable
        source="name"
        variant="outlined"
      />
    ),
  },
  {
    name: 'status',
    label: 'Статусы',
    chipType: 'enumMultiselect',
    chipProps: {
      resource: 'commission_statuses',
      limitTags: 1,
    },
    input: (
      <AutocompleteMultiselectInput
        defaultValue={defaultStatus}
        helperText={false}
        label="Статусы"
        name="status"
        optionText="description"
        optionValue="value"
        options={statusOptions}
      />
    ),
  },
  {
    name: 'merchant',
    label: 'Мерчант',
    chipType: 'autocomplete',
    chipProps: {
      resource: 'merchants',
    },
    input: (
      <ReferenceInput perPage={100} reference="merchants" source="merchant">
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          helperText={false}
          label="Мерчант"
          optionText="name"
          variant="outlined"
        />
      </ReferenceInput>
    ),
  },
  {
    name: 'provider',
    label: 'Провайдеры',
    chipType: 'multiselect',
    chipProps: {
      resource: 'providers',
      limitTags: 1,
    },
    input: (
      <ReferenceArrayInput
        filter={{ 'commissions.merchant': merchantWatched }}
        perPage={100}
        reference="providers"
        sort={{ field: 'name', order: 'ASC' }}
        source="provider"
      >
        <AutocompleteMultiselectInput
          clearOnBlur={false}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label="Провайдеры"
          name="provider"
          optionText="name"
        />
      </ReferenceArrayInput>
    ),
  },
  {
    name: 'currency',
    label: 'Валюты',
    chipType: 'multiselect',
    chipProps: {
      resource: 'currencies',
      limitTags: 1,
    },
    input: (
      <ReferenceArrayInput
        filter={{ active: true }}
        perPage={50}
        reference="currencies"
        source="currency"
      >
        <AutocompleteMultiselectInput
          filterToQuery={(searchText: string) => ({ currency: searchText })}
          getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
          helperText={false}
          label="Валюты"
          name="currency"
        />
      </ReferenceArrayInput>
    ),
  },
  {
    name: 'transactionTypes',
    label: 'Типы транзакций',
    chipType: 'multiselect',
    chipProps: {
      resource: 'transaction_types',
      limitTags: 1,
    },
    input: (
      <ReferenceArrayInput perPage={100} reference="transaction_types" source="transactionTypes">
        <AutocompleteMultiselectInput
          helperText={false}
          label="Типы транзакций"
          name="transactionTypes"
          optionText="name"
        />
      </ReferenceArrayInput>
    ),
  },
  {
    name: 'id',
    label: 'ID комиссии',
    chipType: 'text',
    input: (
      <TextInput
        fullWidth={true}
        helperText={false}
        label="ID комиссии"
        resettable
        source="id"
        variant="outlined"
      />
    ),
  },
  {
    name: 'parent',
    label: 'ID родительской комиссии',
    chipType: 'text',
    input: (
      <TextInput
        fullWidth={true}
        helperText={false}
        label="ID родительской комиссии"
        resettable
        source="parent"
        variant="outlined"
      />
    ),
  },
];
